import React, { memo, useEffect, useMemo } from 'react';
import { useGetEntitlements } from '../../apiHooks/entitlements.hook';
import { useGetProductsStatus } from '../../apiHooks/productsStatus.hook';
import { useMembership } from '../../customHooks/useMembership';
import { useProducts } from '../../customHooks/useProducts';
import { report } from '../../tracking/tracking';
import { BenefitsPage } from './BenefitsPage';
import { useGetSession } from '../../apiHooks/session.hook';
import { useUserInteractions } from '../../customHooks/userInteractions';
import { TrackingEvents } from '../../tracking/trackingEvents';
import { useHasMeinPlus } from '../../customHooks/useHasMeinPlus.hook';
import { useGetIs24SolvencyStatus, useGetSchufaStatus } from '../../apiHooks/solvencyStatus.hook';
import { useGetBenefitsListPage } from '../../customHooks/useGetBenefitsListPage.hook';
import { getBenefitPageState } from '../../utils/getBenefitPageState.util';
import { useGetFeatureSwitches } from '@/apiHooks/featureSwitches.hook';

export const BenefitsPageManager = memo(() => {
  const benefitsListPage = useGetBenefitsListPage();
  const { initializing: isMembershipLoading, isMembershipError } = useMembership();
  const { initializing: isSessionLoading } = useGetSession();
  const { initializing: areFeaturesLoading } = useGetFeatureSwitches();
  const { initializing: areEntitlementsLoading } = useGetEntitlements();
  const { areProductLoading } = useGetProductsStatus();
  const { initializing: areProductsLoading } = useProducts();
  const isMeinPlus = useHasMeinPlus();
  const { initializing: isSchufaInitializing } = useGetSchufaStatus();
  const { initializing: isIs24SolvencyInitializing } = useGetIs24SolvencyStatus();

  // After the products/status endpoint improvement on the backend (see https://gs24.atlassian.net/browse/HB-400) we started seeing some issues popping up on the FE.
  // Mainly post requests to /user-interaction endpoint would fail with status 403. Waiting for requests to both /session and /membership/my to finish loading before calling /user-interaction endpoint
  // fixes the problem.
  const { areUserInteractionsLoading } = useUserInteractions(!isMembershipLoading && !isSessionLoading);

  const isBenefitsPageLoading = useMemo(
    () =>
      areEntitlementsLoading ||
      areProductsLoading ||
      isMembershipLoading ||
      isSessionLoading ||
      areFeaturesLoading ||
      areUserInteractionsLoading ||
      areProductLoading ||
      isSchufaInitializing ||
      isIs24SolvencyInitializing,
    [
      areEntitlementsLoading,
      areProductsLoading,
      isMembershipLoading,
      isSessionLoading,
      areUserInteractionsLoading,
      isSchufaInitializing,
      isIs24SolvencyInitializing,
      areProductLoading,
      areFeaturesLoading,
    ],
  );

  const benefitPageState = useMemo(
    () =>
      getBenefitPageState({
        isLoading: isBenefitsPageLoading,
        isKaeuferPlusError: isMembershipError,
        benefitsListPage,
      }),
    [benefitsListPage, isBenefitsPageLoading, isMembershipError],
  );

  useEffect(() => {
    if (!isBenefitsPageLoading) {
      report(TrackingEvents.LAYOUT, isMeinPlus);
    }
  }, [isMeinPlus, isBenefitsPageLoading]);

  return <BenefitsPage benefitPageState={benefitPageState} />;
});

BenefitsPageManager.displayName = 'BenefitsPageManager';
